import { useState } from "react";
import { trpc } from "@/trpc";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserType, UserStatus } from "@satyrsmc/shared/lib/enums";
import { TYPE_LABELS, STATUS_LABELS } from "./userDisplay";

// Used from the contacts list to invite an unknown person by email.
interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const utils = trpc.useUtils();

  const createInvitation = trpc.admin.users.createInvitation.useMutation({
    onSuccess: () => {
      setSuccess(true);
      setError("");
      void utils.admin.users.listRegistrations.invalidate();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    createInvitation.mutate({
      email,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
    });
  }

  function handleClose(val: boolean) {
    if (!val) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setSuccess(false);
      setError("");
    }
    onOpenChange(val);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite to Sign Up</DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Invitation sent to <strong>{email}</strong>. They will receive an email with a link to
              complete their signup.
            </p>
            <Button onClick={() => handleClose(false)}>Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invite-first-name">First Name</Label>
                <Input
                  id="invite-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-last-name">Last Name</Label>
                <Input
                  id="invite-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createInvitation.isPending}>
                {createInvitation.isPending ? "Sending..." : "Send Invitation"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface CreateContactAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  displayName: string;
  onSuccess?: () => void;
}

export function CreateContactAccountDialog({
  open,
  onOpenChange,
  contactId,
  displayName,
  onSuccess,
}: CreateContactAccountDialogProps) {
  const [userType, setUserType] = useState<UserType>(UserType.User);
  const [userStatus, setUserStatus] = useState<UserStatus>(UserStatus.Active);
  const [adminNote, setAdminNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const utils = trpc.useUtils();

  const inviteContact = trpc.admin.users.inviteContact.useMutation({
    onSuccess: () => {
      setSuccess(true);
      setError("");
      void utils.admin.users.getByContactId.invalidate({ contact_id: contactId });
      void utils.admin.users.getRegistrationByContactId.invalidate({ contact_id: contactId });
      onSuccess?.();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    inviteContact.mutate({
      contact_id: contactId,
      user_type: userType,
      user_status: userStatus,
      admin_note: adminNote || undefined,
      send_email: sendEmail,
    });
  }

  function handleClose(val: boolean) {
    if (!val) {
      setUserType(UserType.User);
      setUserStatus(UserStatus.Active);
      setAdminNote("");
      setSendEmail(true);
      setSuccess(false);
      setError("");
    }
    onOpenChange(val);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create account for {displayName}</DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {sendEmail ? (
                <>
                  Account created and invitation sent to <strong>{displayName}</strong>. They will
                  receive an email with a link to log in.
                </>
              ) : (
                <>
                  Account created for <strong>{displayName}</strong>.
                </>
              )}
            </p>
            <Button onClick={() => handleClose(false)}>Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-account-role">Role</Label>
              <Select value={userType} onValueChange={(v: string) => setUserType(v as UserType)}>
                <SelectTrigger id="create-account-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(UserType).map((t) => (
                    <SelectItem key={t} value={t}>
                      {TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-account-status">Status</Label>
              <Select
                value={userStatus}
                onValueChange={(v: string) => setUserStatus(v as UserStatus)}
              >
                <SelectTrigger id="create-account-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(UserStatus).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-account-admin-note">Admin note</Label>
              <Textarea
                id="create-account-admin-note"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Optional internal note…"
                rows={3}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex items-center gap-2">
              <input
                id="create-account-send-email"
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="size-4 cursor-pointer"
              />
              <Label htmlFor="create-account-send-email" className="font-normal cursor-pointer">
                Send invitation email
              </Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviteContact.isPending}>
                {inviteContact.isPending ? "Creating..." : "Create account"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
